import { httpClient } from './http-client';
import { storageService } from '../../utils/storage';
import type { SuscripcionEstado } from '../../modelos/Auth';
import type { ApiResponse } from './api.types';

export type EstadoSuscripcionPorAdmin = 'Trial' | 'Pagado' | 'Vencido' | 'Cancelado';

export type MetodoPagoSuscripcion = 'Tarjeta' | 'Yape' | 'Plin' | 'Transferencia';

/** Motivo por el que una suscripción quedó en estado "Vencido". */
export type MotivoVencidoSuscripcion = 'PagoFallido' | 'NoRenovado';

export interface SuscripcionPorAdminItem {
  empresaId: string;
  nombre: string;
  ruc: string | null;
  plan: string | null;
  periodo: string | null;
  estado: EstadoSuscripcionPorAdmin;
  activo: boolean;
  precioContratado: number | null;
  ultimosDigitosTarjeta: string | null;
  /** Opcional: el backend aún no lo envía. Cuando exista, la columna "Método" lo usará para distinguir Yape/Plin/Transferencia de Tarjeta. */
  metodoPago?: MetodoPagoSuscripcion | null;
  /** Opcional: el backend aún no lo envía. Próxima fecha de renovación (Pagado) o de vencimiento/fin de acceso (Vencido/Cancelado). */
  fechaRelevante?: string | null;
  /** Opcional: el backend aún no lo envía. Solo aplica cuando estado === 'Vencido'. */
  motivoVencido?: MotivoVencidoSuscripcion | null;
}

export interface ListarSuscripcionesPorAdminParams {
  page?: number;
  pageSize?: number;
  estado?: EstadoSuscripcionPorAdmin;
}

export interface HistorialPagoItem {
  id: string;
  numeroPago: string;
  fechaPago: string;
  monto: number;
  moneda: string;
  medioPago: string;
  ultimosDigitosTarjeta: string | null;
  numeroComprobante: string | null;
  invoiceId: string;
  empresaId: string;
  nombreEmpresa: string;
  rucEmpresa: string | null;
  planNombre: string | null;
  periodicidad: string | null;
}

export type TipoDocumentoInvoice = 'pdf' | 'xml' | 'cdr';

export interface ListarHistorialPagosParams {
  empresaId?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  pagina?: number;
  tamanoPagina?: number;
}

export const suscripcionesService = {
  cargarLote: async (empresaIds: string[], forzar = false): Promise<void> => {
    if (empresaIds.length === 0) return;

    try {
      const response = await httpClient.post<Record<string, SuscripcionEstado>>(
        '/suscripciones/estados-lote',
        empresaIds,
        forzar ? { forzar: true } : undefined,
      );
      if (response.data) {
        storageService.saveSuscripciones(response.data);
      }
    } catch {
      return;
    }
  },

  listarPorAdmin: (params: ListarSuscripcionesPorAdminParams): Promise<ApiResponse<SuscripcionPorAdminItem[]>> =>
    httpClient.get<SuscripcionPorAdminItem[]>('/suscripciones/por-admin', {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      estado: params.estado,
    }),

  listarHistorialPagos: (params: ListarHistorialPagosParams): Promise<ApiResponse<HistorialPagoItem[]>> =>
    httpClient.get<HistorialPagoItem[]>('/suscripciones/historial-pagos', {
      empresaId: params.empresaId,
      fechaDesde: params.fechaDesde,
      fechaHasta: params.fechaHasta,
      pagina: params.pagina ?? 1,
      tamanoPagina: params.tamanoPagina ?? 15,
    }),

  descargarDocumentoInvoice: (invoiceId: string, tipo: TipoDocumentoInvoice = 'pdf'): Promise<Blob> =>
    httpClient.obtenerBlob(`/suscripciones/invoices/${invoiceId}/documento?tipo=${tipo}`),

  cancelar: (empresaId: string): Promise<ApiResponse<void>> =>
    httpClient.post<void>(`/suscripciones/${empresaId}/cancelar`),

  eliminarTarjeta: (empresaId: string): Promise<ApiResponse<void>> =>
    httpClient.delete<void>(`/suscripciones/${empresaId}/tarjeta`),
};
